/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React, { useEffect, useState } from "react"
import { Checkbox, CheckboxGroup, colors, Container, Menu, useCheckboxGroup } from "@carrotfertility/carotene"
import styled from "styled-components"
import { getAvailableParentIdsByBenefitConfig, PARENT_ID_MAPPING } from "../../utils/parentIdMapping"
import { ProviderTypeAnchorButton } from "./ProviderTypeAnchorButton"
import { useUserContext } from "pages/provider-finder/context/UserContext"
import { FertilityWraparoundMessageForDropdown } from "../sidebar/FertilityWraparoundMessage"
import { Benefit } from "pages/provider-finder/types/Benefit"
import { FormattedMessage, useIntl } from "react-intl"
import { useIntlSort } from "../../../../utils/hooks/useIntlSort"

function MenuItem({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Container
      padding="12px 24px"
      minWidth="150px"
      sx={{
        cursor: "pointer",
        ":hover": {
          backgroundColor: colors.base.veryLightCarrotOrange
        },
        ":last-of-type": {
          marginBottom: "12px"
        }
      }}
    >
      {children}
    </Container>
  )
}

const StyledBulkSelectButton = styled.button({
  padding: "12px 24px",
  width: "100%",
  font: "gotham",
  fontSize: "16px",
  fontWeight: "400",
  color: colors.base.carrotOrange,
  background: "none",
  border: "none",
  cursor: "pointer",
  textAlign: "left",
  marginTop: "12px",
  ":hover,&:focus-visible": {
    backgroundColor: colors.base.veryLightCarrotOrange,
    outline: "none"
  }
})

type BulkSelectButtonProps = {
  values: string[]
  children: React.ReactNode
}
export function BulkSelectButton({ values, children }: BulkSelectButtonProps): JSX.Element {
  const state = useCheckboxGroup()
  function onSetValue(values: string[]): void {
    state.setValue(values)
  }
  return (
    <StyledBulkSelectButton type="button" onClick={(_) => onSetValue(values)}>
      {children}
    </StyledBulkSelectButton>
  )
}

type ProviderTypeFilterDropdownProps = {
  providerType: string[]
  setProviderType: React.Dispatch<React.SetStateAction<string[]>>
  benefit: Benefit
}
export function ProviderTypeFilterDropdown({
  providerType,
  setProviderType,
  benefit
}: ProviderTypeFilterDropdownProps): JSX.Element {
  const intl = useIntl()
  const { localeComparator } = useIntlSort()
  const [open, setOpen] = useState(false)
  const { companyCountryCode } = useUserContext()
  const availableParentIds = getAvailableParentIdsByBenefitConfig(benefit)
  const options = availableParentIds
    .sort((parentFullDisplayName1, parentFullDisplayName2) =>
      localeComparator(parentFullDisplayName1, parentFullDisplayName2)
    )
    .map((parentId) => {
      const { fullDisplayName, getSecondaryText } = PARENT_ID_MAPPING[parentId]
      return (
        <MenuItem key={parentId}>
          <Checkbox
            value={parentId}
            secondaryText={
              getSecondaryText && intl.formatMessage(getSecondaryText({ countryCode: companyCountryCode }))
            }
          >
            {intl.formatMessage(fullDisplayName)}
          </Checkbox>
        </MenuItem>
      )
    })

  const onChange = (value): void => {
    setProviderType(value)
  }

  useEffect(() => {
    const handleEsc = (event): void => {
      if (event.keyCode === 27) {
        setOpen(false)
      }
    }
    window.addEventListener("keydown", handleEsc)

    return () => {
      window.removeEventListener("keydown", handleEsc)
    }
  }, [])

  return (
    <Menu
      isDismissable
      onClose={() => setOpen(false)}
      open={open}
      trigger="custom"
      matchWidth
      anchorDisplay="block"
      anchor={<ProviderTypeAnchorButton setOpen={setOpen} open={open} providerType={providerType} benefit={benefit} />}
    >
      <CheckboxGroup
        name="providerType"
        value={providerType}
        onChange={onChange}
        aria-label={intl.formatMessage({ defaultMessage: "Select provider type" })}
      >
        {providerType.length ? (
          <BulkSelectButton values={[]}>
            <FormattedMessage defaultMessage="Unselect All" />
          </BulkSelectButton>
        ) : (
          <BulkSelectButton values={availableParentIds}>
            <FormattedMessage defaultMessage="Select All" />
          </BulkSelectButton>
        )}
        {options}
      </CheckboxGroup>
      {benefit.providerFinder.showFertilityWrapAroundMessage && <FertilityWraparoundMessageForDropdown />}
    </Menu>
  )
}
